import { useCallback, useEffect } from 'react';

import { useNavigate } from '@web/Router/router.hooks';
import {
  useConversationActions,
  useConversationState,
} from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';

export const useNavigateToMessageAction = () => {
  const navigate = useNavigate();
  const { setMarkedMessage } = useConversationActions();
  const {
    firstUnreadMessageId,
    id,
    messages,
    markedMessage: { id: markedMessageId },
    name,
    newestMessageId,
    slug,
  } = useConversationState();

  useEffect(() => {
    if (!markedMessageId) return;
    const isMessageLoaded = messages.visibleIds.includes(markedMessageId);

    if (isMessageLoaded) return;
    const navigatingToNewest = markedMessageId === newestMessageId;

    navigate.toConversationDetails({
      params: {
        id,
        slug,
      },
      queryParams: {
        messageId: navigatingToNewest ? undefined : markedMessageId,
      },
    });
  }, [id, messages, name, navigate, newestMessageId, markedMessageId, slug]);

  const navigateToMessageId = useCallback(
    (messageId: ID, isHighlighted = true) => {
      setMarkedMessage({ id: null, isHighlighted: false });
      setTimeout(() => {
        setMarkedMessage({ id: messageId, isHighlighted });
      });
    },
    [setMarkedMessage]
  );

  const navigateToFirstUnread = useCallback(
    (isHighlighted = true) => {
      navigateToMessageId(firstUnreadMessageId, isHighlighted);
    },
    [firstUnreadMessageId, navigateToMessageId]
  );

  const navigateToNewest = useCallback(
    (isHighlighted = true) => {
      navigateToMessageId(newestMessageId, isHighlighted);
    },
    [navigateToMessageId, newestMessageId]
  );

  return {
    navigateToFirstUnread,
    navigateToMessageId,
    navigateToNewest,
  };
};
